import React, { useEffect, useState, useCallback } from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import { useDispatch } from 'react-redux';
import {
  Box,
  Tooltip,
  IconButton,
  Button,
  InputAdornment,
  Grid,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Collapse
} from '@mui/material';
import { getCityData } from '../../store/Slice/Global/GlobalSlice';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';

export default function CustomerToolbar({
  filterName,
  onFilterName,
  setFilterState,
  filterState,
  setFilterName,
  handleDownload,
  typeIdValue
}) {
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const { CityDrop, LoanTypeDrop } = useGlobalSelector();
  const dispatch = useDispatch();

  const handleGetState = useCallback(
    (optionVal) => {
      dispatch(getCityData({ stateId: optionVal }));
    },
    [dispatch]
  );

  useEffect(() => {
    handleGetState(null);
  }, [handleGetState]);

  const toggleFilter = () => setIsOpenFilter((prev) => !prev);

  const handleRemoveFilter = () => {
    setFilterState({
      cityId: null,
      typeId: null,
      isActive: null
    });
    setFilterName('');
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#ffffff' }}>
      {/* Top Controls Bar */}
      <Box
        sx={{
          minHeight: 64,
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5
        }}
      >
        {/* Left Export Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={() => handleDownload(typeIdValue)}
            sx={{
              borderColor: '#e2e8f0',
              color: '#475569',
              borderRadius: '10px',
              px: 2,
              py: 0.9,
              fontSize: 13.5,
              fontWeight: 600,
              textTransform: 'none',
              bgcolor: '#ffffff',
              '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
            }}
          >
            Export Data
          </Button>
        </Box>

        {/* Right Search & Filter Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TextField
            size="small"
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Customer..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: '100%', sm: 260, md: 280 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#ffffff',
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': {
                  borderColor: '#e2e8f0'
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e1'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0284c7'
                }
              }
            }}
          />

          <Button
            variant="outlined"
            onClick={toggleFilter}
            startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: isOpenFilter ? '#0284c7' : '#475569',
              borderColor: isOpenFilter ? '#0284c7' : '#e2e8f0',
              bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.06)' : '#ffffff',
              height: 40,
              px: 2,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.08)'
              }
            }}
          >
            Filters
          </Button>
        </Box>
      </Box>

      {/* Collapsible Filter Panel */}
      <Collapse in={isOpenFilter}>
        <Box
          sx={{
            p: 2.5,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Product / Loan Type */}
            <Grid item xs={12} sm={4} md={3.5}>
              <Select
                fullWidth
                size="small"
                value={filterState?.typeId ?? ' '}
                onChange={(e) => {
                  const v = e.target.value === ' ' ? null : e.target.value;
                  setFilterState((prev) => ({
                    ...prev,
                    typeId: v
                  }));
                }}
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value=" " disabled sx={{ fontSize: 13 }}>
                  Select Product / Loan Type
                </MenuItem>
                {LoanTypeDrop?.map((i) => (
                  <MenuItem key={i.value} value={i.value} sx={{ fontSize: 13 }}>
                    {i.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* City Autocomplete */}
            <Grid item xs={12} sm={4} md={3.5}>
              <Autocomplete
                disablePortal
                id="combo-box-city"
                size="small"
                options={CityDrop || []}
                getOptionLabel={(option) => option?.label ?? ''}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={CityDrop?.find((opt) => opt.value === filterState?.cityId) ?? null}
                onChange={(e, newValue) => {
                  setFilterState((prev) => ({
                    ...prev,
                    cityId: newValue ? newValue.value : null
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder="Select City"
                    sx={{
                      bgcolor: '#ffffff',
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        fontSize: 13
                      },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                    }}
                  />
                )}
              />
            </Grid>

            {/* Status Select */}
            <Grid item xs={12} sm={3} md={3.5}>
              <Select
                size="small"
                fullWidth
                displayEmpty
                value={filterState?.isActive ?? ''}
                renderValue={(value) => {
                  if (value === '' || value === null) {
                    return <span style={{ color: '#94a3b8' }}>Select Status</span>;
                  }
                  return value === 'true' ? 'Active' : 'Inactive';
                }}
                onChange={(e) => {
                  setFilterState((prev) => ({
                    ...prev,
                    isActive: e.target.value === '' ? null : e.target.value
                  }));
                }}
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value="" sx={{ fontSize: 13 }}>
                  Select Status
                </MenuItem>
                <MenuItem value="true" sx={{ fontSize: 13 }}>
                  Active
                </MenuItem>
                <MenuItem value="false" sx={{ fontSize: 13 }}>
                  Inactive
                </MenuItem>
              </Select>
            </Grid>

            {/* Reset Filter Button */}
            <Grid item xs={12} sm={1} md="auto">
              <Tooltip title="Reset Filter" arrow>
                <IconButton
                  onClick={handleRemoveFilter}
                  size="small"
                  sx={{
                    bgcolor: '#fee2e2',
                    color: '#dc2626',
                    borderRadius: '8px',
                    p: 1,
                    '&:hover': { bgcolor: '#fecaca' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}
